/**
 * @fileoverview Add a referrer without leaving the pipeline (MOR-882)
 *
 * Tagging a job's referrer normally means the contact already exists in the
 * firm directory. This dialog creates one in place — the same
 * `createFirmContact` mutation the directory uses, pinned to the `referrer`
 * category — and hands the new id back so the caller can tag the job with it
 * immediately.
 *
 * Defaults to a person: the usual case is naming the individual who sent
 * the work, then nesting them under the referring firm (existing or typed
 * in as a new organisation) so both the person and the company are recorded.
 */

import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { ContactType } from "@cvx/contacts/firmContacts/schema";

import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { SearchableSelect } from "@/ui/composed/inputs/searchable-select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { extractErrorMessage } from "@/utils/errors";

const NONE_FIRM = "__none__";
const PENDING_FIRM = "__pending__";

export function AddReferrerDialog({
  orgId,
  open,
  onOpenChange,
  onCreated,
}: {
  orgId: Id<"organizations">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired with the new contact so the caller can tag the job with it. */
  onCreated: (firmContactId: Id<"firmContacts">) => void;
}) {
  const createFirmContact = useMutation(
    api.contacts.mutations.createFirmContact,
  );

  const [displayName, setDisplayName] = useState("");
  const [contactType, setContactType] = useState<ContactType>("person");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [parentId, setParentId] = useState<Id<"firmContacts"> | "">("");
  /** Firm name typed in the picker that is not in the directory yet. */
  const [pendingFirmName, setPendingFirmName] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // MOR-995 — referrals often come from several people at the same firm.
  // A person-type referrer can nest under an organisation-type referrer so
  // both the individual and the firm are recorded (and stats roll up).
  const referrers = useQuery(
    api.preAppointment.pipeline.listReferrers,
    open ? { orgId } : "skip",
  );
  const parentOptions = (referrers ?? []).filter(
    (r) => r.contactType === "organisation" && !r.parentFirmContactId,
  );
  const firmSelectOptions = [
    ...parentOptions.map((r) => ({
      value: String(r._id),
      label: r.displayName,
    })),
    ...(pendingFirmName
      ? [{ value: PENDING_FIRM, label: pendingFirmName }]
      : []),
  ];

  const reset = () => {
    setDisplayName("");
    setContactType("person");
    setEmail("");
    setPhone("");
    setParentId("");
    setPendingFirmName(null);
  };

  const handleCreate = async () => {
    const name = displayName.trim();
    if (!name) return;
    setSaving(true);
    try {
      const parentFirmContactId: Id<"firmContacts"> | undefined =
        contactType === "person"
          ? pendingFirmName
            ? await createFirmContact({
                orgId,
                input: {
                  displayName: pendingFirmName,
                  contactType: "organisation",
                  category: "referrer",
                },
              })
            : parentId
              ? parentId
              : undefined
          : undefined;
      const id = await createFirmContact({
        orgId,
        input: {
          displayName: name,
          contactType,
          category: "referrer",
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          parentFirmContactId,
        },
      });
      toast.success(
        parentFirmContactId && pendingFirmName
          ? `${name} added as a referrer at ${pendingFirmName}.`
          : `${name} added as a referrer.`,
      );
      onCreated(id);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not add the referrer."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New referrer</DialogTitle>
          <DialogDescription>
            Adds a contact to the firm directory under the Referrer category and
            tags this job with them.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="referrer-name">Name</Label>
            <Input
              id="referrer-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Susan Smith"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleCreate();
              }}
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="referrer-type">Type</Label>
            <Select
              value={contactType}
              onValueChange={(v) => {
                const next = v as ContactType;
                setContactType(next);
                if (next !== "person") {
                  setParentId("");
                  setPendingFirmName(null);
                }
              }}
            >
              <SelectTrigger id="referrer-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="person">Person</SelectItem>
                <SelectItem value="organisation">Organisation</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {contactType === "person" && (
            <div className="grid gap-1.5">
              <Label>Firm</Label>
              <SearchableSelect
                aria-label="Firm"
                value={
                  pendingFirmName
                    ? PENDING_FIRM
                    : parentId
                      ? String(parentId)
                      : NONE_FIRM
                }
                onValueChange={(v) => {
                  if (v === NONE_FIRM) {
                    setParentId("");
                    setPendingFirmName(null);
                    return;
                  }
                  if (v === PENDING_FIRM) return;
                  setParentId(v as Id<"firmContacts">);
                  setPendingFirmName(null);
                }}
                options={firmSelectOptions}
                allOptionLabel="No firm"
                allOptionValue={NONE_FIRM}
                placeholder="No firm"
                searchPlaceholder="Search or add a firm…"
                emptyMessage="No matching firms."
                onCreateOption={(name) => {
                  setPendingFirmName(name);
                  setParentId("");
                }}
                createOptionLabel={(name) => `Add "${name}"`}
                className="h-10 w-full text-sm"
              />
              <p className="text-xs text-muted-foreground">
                The company this person works at. Type a name to add a new one —
                referral stats roll up to the firm as well.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="referrer-email">Email</Label>
              <Input
                id="referrer-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="referrer-phone">Phone</Label>
              <Input
                id="referrer-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={saving || !displayName.trim()}
            onClick={() => void handleCreate()}
          >
            {saving ? "Adding…" : "Add referrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
